import { JhButton } from "designPackages/buttonPackage";
import { JhText } from "designPackages/textPackage";
import JhSelectWrapper from "Tailwind/components/design-packages/JhSelectWrapper";
import { RefreshCw, Plus } from "lucide-react";
import { dateFilterList } from "services/constants";
import { useRouter } from "next/router";
import { Routes } from "routes/RouteConstants";
import LogService from "services/LogService";
import CREDIT_EVENTS from "utils/CreditEvents";
import { useIsMobile } from "hooks/useDeviceType";
import { handleAddCredits } from "./utils";

interface tabOptions {
  value: string;
  label: string;
  count?: number;
}

interface TransactionHistoryV2ToolbarProps {
  dateFilter: number;
  onDateFilterChange: (dateValue: string) => void;
  onRefresh: () => void;
  isLoading?: boolean;
  selectedTab?: string;
  onTabChange?: (filter: string) => void;
  tabOptions?: tabOptions[];
}

const TransactionHistoryV2Toolbar = ({
  dateFilter,
  onDateFilterChange,
  onRefresh,
  isLoading = false,
  selectedTab = "ALL",
  onTabChange,
  tabOptions = []
}: TransactionHistoryV2ToolbarProps) => {
  const router = useRouter();
  const { query } = router;
  const isMobile = useIsMobile();

  // Convert dateFilterList to SelectOption format
  const dateOptions = dateFilterList.map(item => ({
    value: item.value,
    label: item.label
  }));

  const handleDateSelect = (dateValue: string) => {
    const numericValue = Number(dateValue);
    const queryParams = { ...query, dateId: numericValue, page: 1 };
    router.push({
      pathname: Routes.TRANSACTION_HISTORY,
      query: queryParams
    });
    LogService.log(CREDIT_EVENTS.TH_DATE_FILTER_SELECTED, {
      date: numericValue
    });
    onDateFilterChange(dateValue);
  };

  const handleRefresh = () => {
    LogService.log(CREDIT_EVENTS.TH_REFRESH_CLICKED);
    onRefresh();
  };

  if (isMobile) {
    return (
      <div id="th-v2-toolbar-mobile-container" className="flex gap-3 w-full pb-0">
        {/* Date Filter - Half width */}
        <div id="th-v2-toolbar-mobile-date-filter" className="flex-1">
          <JhSelectWrapper
            value={dateFilter}
            options={dateOptions}
            onValueChange={handleDateSelect}
            placeholder=""
            triggerClassName="h-9 text-sm"
          />
        </div>

        {/* Transaction Filter - Half width */}
        <div id="th-v2-toolbar-mobile-transaction-filter" className="flex-1">
          <JhSelectWrapper
            value={selectedTab}
            options={tabOptions.map(option => ({
              value: option.value,
              label: option.label,
              count: option.count,
              disabled: option.count === 0
            }))}
            onValueChange={onTabChange || (() => { })}
            placeholder=""
            triggerClassName="h-9 text-sm"
            showCountInOptions={true}
          />
        </div>
      </div>
    );
  }

  // Desktop: Show dropdowns on left, buttons on right
  return (
    <div id="th-v2-toolbar-desktop-container" className="w-full flex items-center justify-between gap-4">
      {/* Left side - Date Filter */}
      <div id="th-v2-toolbar-desktop-date-filter" className="w-40 bg-white rounded-md">
        <JhSelectWrapper
          value={dateFilter}
          options={dateOptions}
          onValueChange={handleDateSelect}
          placeholder="Select date range"
          triggerClassName="h-9 text-sm"
        />
      </div>

      {/* Right side - Action Buttons */}
      <div id="th-v2-toolbar-desktop-actions" className="flex items-center gap-3">
        <JhButton
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          className="flex gap-1"
        >
          <RefreshCw className="w-4 h-4 text-foreground" />
          Refresh
        </JhButton>

        <JhButton
          variant="default"
          size="sm"
          onClick={handleAddCredits(router)}
          className="flex gap-1"
        >
          <Plus className="w-4 h-4 text-background" />
          Add Credits
        </JhButton>
      </div>
    </div>
  );
};

export default TransactionHistoryV2Toolbar;
